import { useState, useRef, useEffect } from 'react';
import { Search, Star, Bell } from 'lucide-react';

interface NewsItem {
    id: number;
    title: string;
    category: string;
    date: string;
    summary: string;
    isEvent?: boolean;
}

interface EventNews {
    id: number;
    title: string;
    emoji: string;
}

const eventNews: EventNews[] = [
    {
        id: 1,
        title: "SpaceX完成新一轮融资，估值突破2100亿美元",
        emoji: "🚀"
    },
    {
        id: 2,
        title: "OpenAI发布GPT-5，AI能力再次突破，估值达到1500亿美元",
        emoji: "🤖"
    },
    {
        id: 3,
        title: "ByteDance东南亚业务增长迅猛，估值突破2500亿美元",
        emoji: "📱"
    },
    {
        id: 4,
        title: "Anthropic获得重大融资，AI竞争格局生变",
        emoji: "💡"
    }
];

const mockNews: NewsItem[] = [
    {
        id: 1,
        title: "SpaceX完成新一轮融资，估值突破2100亿美元",
        category: "精选",
        date: "2024-03-15",
        summary: "太空探索技术公司SpaceX在最新一轮融资中估值达到2100亿美元，创下商业航天领域新纪录。",
        isEvent: true
    },
    {
        id: 2,
        title: "OpenAI发布GPT-5，AI能力再次突破",
        category: "动态跟踪",
        date: "2024-03-14",
        summary: "OpenAI最新发布的GPT-5模型在多个领域展现出突破性进展，进一步扩大了AI领域的领先优势。"
    },
    {
        id: 3,
        title: "ByteDance在东南亚市场份额持续增长",
        category: "Watchlist",
        date: "2024-03-13",
        summary: "字节跳动旗下产品在东南亚市场的用户数量和营收双双实现显著增长。"
    }
];

function News() {
    const [activeTab, setActiveTab] = useState('全部');
    const [searchTerm, setSearchTerm] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef<number>();
    const startTimeRef = useRef<number>();
    const currentTranslateRef = useRef(0);

    const tabs = ['全部', '精选', '动态跟踪', 'Watchlist'];

    useEffect(() => {
        if (!scrollRef.current || !containerRef.current) return;

        const scrollContainer = scrollRef.current;
        const duration = 14000; // 完整滚动一次的时间（毫秒）

        const animate = (timestamp: number) => {
            if (!startTimeRef.current) {
                startTimeRef.current = timestamp;
            }

            const elapsed = timestamp - startTimeRef.current;
            const progress = (elapsed % duration) / duration;

            // 获取第一组消息的总宽度
            const firstGroupWidth = scrollContainer.children[0].getBoundingClientRect().width;

            // 计算当前位移
            currentTranslateRef.current = -progress * firstGroupWidth;

            // 当第二组消息刚好到达容器左侧时重置
            if (currentTranslateRef.current <= -firstGroupWidth) {
                // 移除过渡动画
                scrollContainer.style.transition = 'none';
                currentTranslateRef.current = 0;
                startTimeRef.current = timestamp;
                // 恢复过渡动画
                scrollContainer.style.transition = 'transform 0.1s linear';
            }

            // 应用位移
            scrollContainer.style.transform = `translateX(${currentTranslateRef.current}px)`;

            // 继续动画
            animationRef.current = requestAnimationFrame(animate);
        };

        // 开始动画
        animationRef.current = requestAnimationFrame(animate);

        // 清理函数
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    const filteredNews = mockNews.filter(news =>
        (activeTab === '全部' || news.category === activeTab) &&
        (news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            news.summary.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="min-h-screen bg-black">
            {/* Continuous Scrolling Event Banner */}
            <div className="bg-blue-600 text-white py-2 overflow-hidden">
                <div className="container mx-auto relative">
                    <div
                        ref={containerRef}
                        className="relative overflow-hidden"
                    >
                        <div
                            ref={scrollRef}
                            className="flex whitespace-nowrap"
                            style={{
                                willChange: 'transform',
                            }}
                        >
                            {/* 第一组消息 */}
                            <div className="flex whitespace-nowrap">
                                {eventNews.map((event, index) => (
                                    <div
                                        key={`${event.id}-${index}`}
                                        className="inline-block px-8"
                                    >
                                        <span>{event.emoji} {event.title}</span>
                                    </div>
                                ))}
                            </div>
                            {/* 第二组消息（用于无缝滚动） */}
                            <div className="flex whitespace-nowrap">
                                {eventNews.map((event, index) => (
                                    <div
                                        key={`${event.id}-${index}-clone`}
                                        className="inline-block px-8"
                                    >
                                        <span>{event.emoji} {event.title}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {/* Search Bar */}
                <div className="relative mb-6">
                    <input
                        type="text"
                        placeholder="搜索新闻..."
                        className="w-full bg-gray-900 text-white rounded-full py-3 px-6 pl-12"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>

                {/* Tabs */}
                <div className="flex space-x-4 mb-6 overflow-x-auto pb-2">
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            className={`px-6 py-2 rounded-full whitespace-nowrap ${
                                activeTab === tab
                                    ? 'bg-white text-black'
                                    : 'text-white bg-gray-900 hover:bg-gray-800'
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* News List */}
                <div className="space-y-4">
                    {filteredNews.map(news => (
                        <div key={news.id} className="bg-gray-900 rounded-xl p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center space-x-3 mb-1.5">
                                        {news.category === '精选' && <Star className="text-yellow-400" size={16} />}
                                        <span className="text-blue-400">{news.category}</span>
                                        <span className="text-gray-400">{news.date}</span>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-1.5">{news.title}</h3>
                                    <p className="text-gray-400">{news.summary}</p>
                                </div>
                                <button className="text-gray-400 hover:text-white">
                                    <Bell size={20} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default News;
